import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';

const FORM_URL = 'https://readdy.ai/api/form/d9orjkgsenbnioleskj0';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 韩漫在线观看';
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    const formData = new FormData(form);
    formData.delete('website_alt');
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* empty */ }
      if (res.ok && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed as { meta?: { message?: string; detail?: string }; message?: string }).meta?.message
          || (parsed as { meta?: { message?: string; detail?: string }; message?: string }).meta?.detail
          || (parsed as { message?: string }).message
          || responseText;
        if (typeof serverMsg === 'string' && serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后再试');
        } else {
          setFormStatus('error');
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <section className="relative py-16 md:py-24 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <nav className="mb-4" aria-label="面包屑导航">
              <ol className="inline-flex items-center gap-2 text-sm text-foreground-500">
                <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link></li>
                <li className="text-foreground-300">/</li>
                <li className="text-foreground-700">关于我们</li>
              </ol>
            </nav>
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-4">
              关于韩漫在线观看
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              致力于为广大韩漫爱好者提供最优质的免费漫画下拉式阅读体验，打造最稳定、最便捷的韩漫漫画网站
            </p>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50" aria-labelledby="brand-title">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 id="brand-title" className="font-heading text-2xl md:text-3xl text-foreground-900 mb-8 text-center">
              品牌故事
            </h2>
            <div className="space-y-6 text-sm md:text-base text-foreground-600 leading-relaxed">
              <p>
                韩漫在线观看成立于2024年，由一群热爱韩国漫画的年轻人共同创建。我们深知韩漫爱好者在寻找稳定、高质量的漫画阅读平台时所面临的困难——碎片化的资源、频繁的广告干扰、不稳定的访问体验，这些问题严重影响了阅读的乐趣。
              </p>
              <p>
                为此，我们倾注全部心血打造了韩漫在线观看这个平台，以<strong>免费漫画下拉式阅读</strong>为核心特色，为用户提供沉浸式、无干扰的韩漫阅读体验。通过精心优化的<strong>漫画网址</strong>服务架构，我们的<strong>韩漫漫画网站</strong>能够确保7×24小时稳定运行，让每一位访问者都能随时随地享受流畅的<strong>韩漫在线观看</strong>服务。
              </p>
              <p>
                经过团队的不懈努力，韩漫在线观看已经发展成为拥有数万部韩漫资源、覆盖爱情、奇幻、动作、悬疑、搞笑、剧情、恐怖等全品类的综合性韩漫平台。我们与多家韩国漫画工作室保持紧密合作，确保能够在第一时间为用户带来最新的韩漫章节更新。
              </p>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100" aria-labelledby="reputation-title">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 id="reputation-title" className="font-heading text-2xl md:text-3xl text-foreground-900 mb-10 text-center">
              信誉与数据
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {[
                { icon: 'ri-book-open-line', value: '10,000+', label: '韩漫资源' },
                { icon: 'ri-user-heart-line', value: '500万+', label: '忠实读者' },
                { icon: 'ri-shield-check-line', value: '99.9%', label: '稳定运行' },
                { icon: 'ri-timer-line', value: '每日更新', label: '从不间断' },
              ].map((stat, i) => (
                <div key={i} className="bg-background-50 rounded-xl p-6 text-center border border-background-200/70">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className={`${stat.icon} text-xl`} style={{ width: '24px', height: '24px' }}></i>
                  </div>
                  <div className="font-heading text-2xl md:text-3xl text-foreground-900 mb-1">{stat.value}</div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50" aria-labelledby="advantages-title">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 id="advantages-title" className="font-heading text-2xl md:text-3xl text-foreground-900 mb-10 text-center">
              为什么选择我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              {[
                { icon: 'ri-speed-up-line', title: '极速加载', desc: '采用全球CDN加速与智能缓存技术，确保漫画图片秒级加载，下拉式阅读流畅不卡顿。' },
                { icon: 'ri-smartphone-line', title: '多端适配', desc: '全面适配PC、平板、手机等设备，无论在哪里都能获得最佳的韩漫阅读体验。' },
                { icon: 'ri-download-cloud-2-line', title: '每日更新', desc: '与韩国原版同步更新，热门连载作品24小时内完成翻译同步，追更不等待。' },
                { icon: 'ri-shield-flash-line', title: '安全稳定', desc: '稳定服务器架构、多重安全防护，保障您的阅读体验安全无虞，远离恶意广告和弹窗。' },
                { icon: 'ri-palette-line', title: '精美画质', desc: '支持高清原画质阅读，每一格漫画都清晰细腻，还原韩国漫画的精致艺术风格。' },
                { icon: 'ri-emotion-happy-line', title: '纯净体验', desc: '清爽的界面设计，无弹窗广告干扰，让您专注于漫画内容本身，享受纯粹的阅读乐趣。' },
              ].map((adv, i) => (
                <div key={i} className="flex gap-4 p-5 bg-background-100 rounded-xl border border-background-200/70">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className={`${adv.icon} text-lg`} style={{ width: '20px', height: '20px' }}></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-base text-foreground-900 mb-1.5">{adv.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{adv.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100" aria-labelledby="contact-title">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <h2 id="contact-title" className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-8">
              有任何问题或建议？请通过以下表单与我们联系，我们会在24小时内回复
            </p>

            {formStatus === 'success' ? (
              <div className="bg-accent-50 border border-accent-200 rounded-xl p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                  <i className="ri-check-line text-2xl" style={{ width: '28px', height: '28px' }}></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">发送成功！</h3>
                <p className="text-sm text-foreground-600">感谢您的留言，我们会尽快回复您</p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form=""
                className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8 space-y-5"
                noValidate
              >
                {formStatus === 'error' && formError && (
                  <div className="bg-primary-50 border border-primary-200 text-primary-700 text-sm rounded-lg p-4">
                    {formError}
                  </div>
                )}
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-label text-foreground-700 mb-1.5">
                    您的称呼 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的称呼"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-label text-foreground-700 mb-1.5">
                    电子邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-label text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    className="w-full px-4 py-2.5 text-sm bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-label text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-2.5 text-sm bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言内容（不超过500字）"
                  ></textarea>
                </div>
                <input
                  type="text"
                  name="website_alt"
                  className="anti-spam-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 text-white rounded-lg font-label text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  发送留言
                </button>
              </form>
            )}

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: 'ri-mail-line', title: '电子邮箱', value: 'contact@hanmancomic.com' },
                { icon: 'ri-customer-service-2-line', title: '在线客服', value: '7×24小时在线' },
                { icon: 'ri-global-line', title: '官方网站', value: 'www.hanmancomic.com' },
              ].map((info, i) => (
                <div key={i} className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70">
                  <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500">
                    <i className={`${info.icon} text-base`} style={{ width: '18px', height: '18px' }}></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">{info.title}</p>
                    <p className="text-sm text-foreground-800 font-medium">{info.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于韩漫在线观看',
            description: '韩漫在线观看致力于为韩漫爱好者提供最优质的免费漫画下拉式阅读体验，打造最稳定便捷的韩漫漫画网站。',
            url: 'https://www.hanmancomic.com/about',
            isPartOf: { '@type': 'WebSite', name: '韩漫在线观看', url: 'https://www.hanmancomic.com' },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.hanmancomic.com/' },
              { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.hanmancomic.com/about' },
            ],
          }),
        }}
      />
    </>
  );
}